/**
 * buildReactionSchemePackage - PIPH-96 Reaction Scheme packaging for Piph Author LLM
 *
 * Assembles the user message sent to Claude's API for reaction scheme alt text generation.
 * Wraps template output from both reactant and product sides with reaction conditions
 * into a single structured input.
 *
 * @package @alchemiesolns/svg-interactive
 * @see PIPH-96
 */

import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { WorkspaceFeatureResult } from '@alchemiesolns/feature-extractor-compat';
import { findConnectedComponents } from './graphUtils';
import { buildStructuredAltText } from './buildStructuredAltText';
import { buildStereoSpatialBlock } from './stereoSpatialBlock';
import { buildCurvedArrowBlock } from './curvedArrowBlock';

/**
 * Reaction scheme arrow conditions
 */
export interface ReactionArrowConditions {
  /** Text displayed above the arrow (e.g., "NaBH₄") */
  aboveText?: string;
  /** Text displayed below the arrow (e.g., "CH₃OH") */
  belowText?: string;
}

/**
 * Product specification - can be structured, simple, unknown, or raw text
 */
export type ProductSpec =
  | { type: 'workspace'; workspace: MolecularWorkspace; featureResult: WorkspaceFeatureResult }
  | { type: 'unknown' }  // Renders as "Products: ?"
  | { type: 'rawText'; text: string };  // Author-entered text

/**
 * Reactant specification - structured workspace or author-entered text.
 *
 * No 'unknown' variant: reactants are always specified by the author;
 * "predict the product" only makes sense on the product side.
 */
export type ReactantSpec =
  | { type: 'workspace'; workspace: MolecularWorkspace; featureResult: WorkspaceFeatureResult }
  | { type: 'rawText'; text: string };

/**
 * Options for buildReactionSchemePackage
 */
export interface ReactionSchemePackageOptions {
  /** Include stereo spatial context blocks for reactants */
  stereoSpatialReactants?: boolean;
  /** Include curved arrow descriptions for reactants */
  curvedArrowsReactants?: boolean;
  /** Include stereo spatial context blocks for products */
  stereoSpatialProducts?: boolean;
}

/**
 * Internal representation of a molecule during processing
 */
interface MoleculeData {
  atomCount: number;
  altText: string;
  isSimple: boolean;
  originalIndex: number;
}

/**
 * Symbol replacement map for screen reader clarity
 * Per PIPH-96 spec
 */
const SYMBOL_REPLACEMENTS: Array<[string | RegExp, string]> = [
  [/[Δδ]/g, 'heat'],           // Delta symbol -> "heat"
  [/·/g, 'dot'],               // Middle dot -> "dot"
  [/≡/g, 'triple bond'],       // Triple bond symbol
  [/(?<!\d)=(?!\d)/g, 'double bond'], // Equals (not in formulas) -> "double bond"
  [/hν/g, 'light'],            // Photochemical notation
  [/hv/gi, 'light'],           // Alternative photochemical notation
];

/**
 * Format arrow conditions text with symbol replacements for screen reader clarity.
 *
 * @param text - Raw condition text (e.g., "Δ, H₂O")
 * @returns Formatted text with symbols replaced (e.g., "heat, H₂O")
 */
export function formatArrowConditions(text: string): string {
  if (!text) return '';

  let result = text;
  for (const [pattern, replacement] of SYMBOL_REPLACEMENTS) {
    result = result.replace(pattern, replacement);
  }

  return result.trim();
}

/**
 * Build the arrow line for reaction conditions.
 *
 * @param conditions - Above and below arrow text
 * @returns Formatted arrow line, or empty string if no conditions
 */
function buildArrowLine(conditions: ReactionArrowConditions): string {
  const above = formatArrowConditions(conditions.aboveText ?? '');
  const below = formatArrowConditions(conditions.belowText ?? '');

  // If both empty, omit arrow line entirely
  if (!above && !below) {
    return '';
  }

  // Build arrow description
  const parts: string[] = [];
  if (above) {
    parts.push(`${above} above`);
  }
  if (below) {
    parts.push(`${below} below`);
  }

  return `Arrow: ${parts.join(', ')}`;
}

/**
 * Check if alt text represents a simple molecule (water, HCl, etc.)
 *
 * Simple molecules have no "Core:" line and no newlines - just a name.
 */
function isSimpleMolecule(altText: string): boolean {
  return !altText.includes('\n') && !altText.startsWith('Core:');
}

/**
 * Build a WorkspaceFeatureResult for a single molecule (connected component)
 * by filtering the full workspace result to only include features belonging
 * to atoms in this molecule.
 */
function buildMoleculeFeatureResult(
  fullResult: WorkspaceFeatureResult,
  moleculeAtomIds: Set<string>
): WorkspaceFeatureResult {
  // Filter features to only those with atoms in this molecule
  const moleculeFeatures = fullResult.features.filter(wf =>
    wf.atomIds.some(atomId => moleculeAtomIds.has(atomId))
  );

  return {
    features: moleculeFeatures,
    graph: fullResult.graph,
    indexToAtomId: fullResult.indexToAtomId,
  };
}

/**
 * Build a molecule block (Starting materials or Products) from a workspace.
 *
 * @param workspace - The molecular workspace
 * @param featureResult - Output from extractWorkspaceFeatures()
 * @param header - Section header (e.g., "Starting materials" or "Products")
 * @returns Formatted molecule block
 */
function buildMoleculeBlock(
  workspace: MolecularWorkspace,
  featureResult: WorkspaceFeatureResult,
  header: string
): string {
  // Find connected components (molecules)
  const components = findConnectedComponents(workspace);

  // Handle empty workspace
  if (components.length === 0) {
    return `${header}:\nEmpty`;
  }

  // Process each molecule
  const molecules: MoleculeData[] = components.map((component, index) => {
    const moleculeAtomIds = new Set(component.atomIds);

    // Build feature result for this molecule
    const moleculeResult = buildMoleculeFeatureResult(featureResult, moleculeAtomIds);

    // PIPH-140: Get all atom symbols and net charge for simple molecules
    const allAtomSymbols = component.atomIds.map(atomId => {
      const atom = workspace.getAtom(atomId);
      return atom?.symbol ?? '';
    }).filter(Boolean);

    const netCharge = component.atomIds.reduce(
      (sum, id) => sum + (workspace.getAtom(id)?.charge ?? 0), 0
    );

    // Generate alt text for this molecule. `buildStructuredAltText` builds
    // its own per-molecule AtomLabelResolver from `moleculeResult`.
    const altText = buildStructuredAltText(moleculeResult, allAtomSymbols, netCharge);

    return {
      atomCount: component.atomIds.length,
      altText,
      isSimple: isSimpleMolecule(altText),
      originalIndex: index,
    };
  });

  // Sort by atom count (largest first), preserve original order for ties
  molecules.sort((a, b) => {
    const atomDiff = b.atomCount - a.atomCount;
    if (atomDiff !== 0) return atomDiff;
    return a.originalIndex - b.originalIndex;
  });

  // Format output
  const output: string[] = [];

  if (molecules.length === 1) {
    // Single molecule - no "Molecule N:" label
    output.push(`${header}:`);
    output.push(molecules[0].altText);
  } else {
    // Multiple molecules - count in header + labeled blocks
    output.push(`${header}: ${molecules.length} molecules`);
    output.push('');

    for (let i = 0; i < molecules.length; i++) {
      const mol = molecules[i];
      const label = `Molecule ${i + 1}`;

      if (mol.isSimple) {
        // Simple molecule: inline format
        output.push(`${label}: ${mol.altText}`);
      } else {
        // Complex molecule: block format
        output.push(`${label}:`);
        output.push(mol.altText);
      }

      // Add blank line between molecules (but not after the last one)
      if (i < molecules.length - 1) {
        output.push('');
      }
    }
  }

  return output.join('\n');
}

/**
 * Build products block based on ProductSpec type.
 *
 * @param products - Product specification
 * @returns Formatted products block
 */
function buildProductsBlock(products: ProductSpec): string {
  switch (products.type) {
    case 'unknown':
      return 'Products: ?';

    case 'rawText':
      return `Products: ${products.text}`;

    case 'workspace':
      return buildMoleculeBlock(products.workspace, products.featureResult, 'Products');
  }
}

/**
 * Build starting materials block based on ReactantSpec type.
 */
function buildStartingMaterialsBlock(reactants: ReactantSpec): string {
  switch (reactants.type) {
    case 'rawText':
      return `Starting materials: ${reactants.text}`;

    case 'workspace':
      return buildMoleculeBlock(reactants.workspace, reactants.featureResult, 'Starting materials');
  }
}

/**
 * Build LLM-ready text package for a reaction scheme.
 *
 * Format (per PIPH-96 spec):
 * ```
 * Reaction scheme
 *
 * Starting materials:
 * {reactant block}
 *
 * Arrow: {conditions}
 *
 * Products:
 * {product block}
 * ```
 *
 * @param reactantWorkspace - Workspace containing starting materials
 * @param reactantFeatures - Feature extraction result for reactants
 * @param arrowConditions - Reagents/conditions above/below arrow
 * @param products - Product specification (workspace, unknown, or raw text)
 * @param options - Additional options (stereo spatial, curved arrows)
 * @returns Formatted string for LLM consumption
 *
 * @example Simple transformation
 * ```
 * Reaction scheme
 *
 * Starting materials:
 * Core: 6-membered ring
 *
 * 6-membered ring:
 * - Ketone at carbon-1
 *
 * Arrow: NaBH₄ above, CH₃OH below
 *
 * Products:
 * Core: 6-membered ring
 *
 * 6-membered ring:
 * - Alcohol at carbon-1
 * ```
 *
 * @example Predict the product
 * ```
 * Reaction scheme
 *
 * Starting materials:
 * Core: benzene ring
 *
 * Benzene ring:
 * - Chlorine at carbon-1
 *
 * Arrow: H₂SO₄ above, heat below
 *
 * Products: ?
 * ```
 */
export function buildReactionSchemePackage(
  reactants: ReactantSpec,
  arrowConditions: ReactionArrowConditions,
  products: ProductSpec,
  options?: ReactionSchemePackageOptions
): string {
  const output: string[] = [];

  // Header
  output.push('Reaction scheme');
  output.push('');

  // Starting materials block
  output.push(buildStartingMaterialsBlock(reactants));

  // Context add-ons for reactants only apply when reactants are a workspace
  if (reactants.type === 'workspace') {
    if (options?.curvedArrowsReactants) {
      const arrowBlock = buildCurvedArrowBlock(reactants.workspace, reactants.featureResult);
      if (arrowBlock) {
        output.push('');
        output.push(arrowBlock);
      }
    }

    if (options?.stereoSpatialReactants) {
      const stereoBlock = buildStereoSpatialBlock(reactants.workspace, reactants.featureResult);
      if (stereoBlock) {
        output.push('');
        output.push(stereoBlock);
      }
    }
  }

  // Arrow line (if conditions present)
  const arrowLine = buildArrowLine(arrowConditions);
  if (arrowLine) {
    output.push('');
    output.push(arrowLine);
  }

  // Products block
  output.push('');
  const productsBlock = buildProductsBlock(products);
  output.push(productsBlock);

  // Append stereo spatial for products if requested and products is a workspace
  if (options?.stereoSpatialProducts && products.type === 'workspace') {
    const stereoBlock = buildStereoSpatialBlock(products.workspace, products.featureResult);
    if (stereoBlock) {
      output.push('');
      output.push(stereoBlock);
    }
  }

  return output.join('\n').trimEnd();
}
